// Which level content this player is playing: the release row id and its
// revision, and the collection version that revision was cut from — exactly
// the numbers the server journals for every content change
// (BlockRoyale-server core/contentVersions.ts). Analytics puts them on every
// event, so a player's data can be joined to "release 14 rev 3" directly.
//
// A leaf module on purpose: LevelPackService (inside the level_config import
// cycle) writes it and the analytics service reads it, without either one
// importing the other.

export interface LevelContentIdentity
{
  release_id: number | null;
  release_rev: number | null;
  collection: string | null;
  collection_version: number | null;
}

let current: LevelContentIdentity = { release_id: null, release_rev: null, collection: null, collection_version: null };

function intOrNull( value: unknown ): number | null
{
  return typeof value === "number" && Number.isInteger( value ) ? value : null;
}

/** Takes the identity a level-release response carries (release_id, revision, collection, collection_version); null clears it. */
export function setLevelContentIdentity( blob: unknown ): void
{
  const raw = ( blob && typeof blob === "object" ? blob : {} ) as Record<string, unknown>;
  current = {
    release_id: intOrNull( raw.release_id ),
    release_rev: intOrNull( raw.revision ),
    collection: typeof raw.collection === "string" ? raw.collection : null,
    collection_version: intOrNull( raw.collection_version ),
  };
}

/**
 * Whether a cached release/manifest carries the identity fields. A copy saved
 * by an older build does not, and must be refetched rather than revalidated.
 */
export function hasContentIdentity( blob: unknown ): boolean
{
  return !!blob && typeof blob === "object" && typeof ( blob as Record<string, unknown> ).release_id === "number";
}

/** Event properties, or nothing before a release (or from a server too old to send one) is loaded. */
export function levelContentProperties(): Record<string, string | number | null>
{
  if ( current.release_id === null )
    return {};
  return { ...current };
}
